import { Box, Group, Text, TextInput } from "@mantine/core"
import { clsx } from "@mantine/styles"
import { useIsMobile } from "@utils/device.utils"
import { type FC, useState } from "react"
import styles from "./OTPInput.module.css"

interface OTPInputProps {
	disabled?: boolean
	onChange: (otp: string) => void
	error?: string
	success?: string
}

const OTPInput: FC<OTPInputProps> = ({
	disabled,
	onChange,
	error,
	success
}) => {
	const [otp, setOtp] = useState(Array(6).fill(""))
	const isMobile = useIsMobile()

	const updateOtp = (newOtp: string[]) => {
		setOtp(newOtp)
		onChange(newOtp.join(""))
	}

	const handleKeyDown = (
		index: number,
		event: React.KeyboardEvent<HTMLInputElement>
	) => {
		const newOtp = [...otp]
		if (event.key === "Backspace") {
			newOtp[index] = ""
			updateOtp(newOtp)
			if (index > 0) {
				const prevInput = document.getElementById(`otp-input-${index - 1}`)
				prevInput?.focus()
			}
		}
		if (/^[0-9]$/.test(event.key) || event.key === "") {
			newOtp[index] = event.key

			if (index < 5) {
				for (let i = index + 1; i < 6; i++) {
					newOtp[i] = ""
				}
			}

			updateOtp(newOtp)

			if (index < 5) {
				const nextInput = document.getElementById(`otp-input-${index + 1}`)
				nextInput?.focus()
			}
		}
	}

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		const rawData = event.clipboardData.getData("text")
		const digitsOnly = rawData.replace(/\D/g, "").slice(0, 6)
		const newOtp = [...otp]

		for (let i = 0; i < digitsOnly.length; i++) {
			if (i < 6) {
				newOtp[i] = digitsOnly[i]
			}
		}
		updateOtp(newOtp)
	}

	const inputHeight = isMobile ? 50 : 80

	return (
		<Box p={0}>
			<Group wrap="nowrap" justify="center">
				{otp.map((digit, index) => (
					<TextInput
						ta={"center"}
						key={index}
						id={`otp-input-${index}`}
						maxLength={1}
						h={inputHeight}
						value={digit}
						disabled={disabled || !!success}
						onChange={() => {}}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={(e) => handlePaste(e)}
						className={clsx(
							styles.otpInput,
							error ? styles.error : success ? styles.success : ""
						)}
						styles={{
							input: {
								p: 0,
								height: inputHeight,
								border: "none",
								opacity: disabled ? 0.6 : 1,
								cursor: disabled ? "not-allowed" : "text",
								textAlign: "center"
							}
						}}
					/>
				))}
			</Group>
			{error && (
				<Text c="royal.52" size="sm" ta="center" mt="xs">
					{error}
				</Text>
			)}
			{success && (
				<Text c="royal.49" size="sm" ta="center" mt="xs">
					{success}
				</Text>
			)}
		</Box>
	)
}

export default OTPInput
